import { useCallback, useMemo, useState } from "react";

/**
 * useBulkSelect — lightweight multi-select state for admin lists.
 *
 * Returns:
 *   selected: Set<id>                                  — currently selected IDs
 *   isSelected(id): boolean
 *   toggle(id): void                                   — flip selection of a single row
 *   clear(): void
 *   selectAll(ids): void                               — pass the full visible-id list
 *   allSelected: boolean                                — true when `selected` covers all `visibleIds`
 *   someSelected: boolean                               — indeterminate state for the header checkbox
 *   count: number
 *   toggleAll(ids): void                                — convenience: if allSelected → clear, else selectAll
 *
 * Usage:
 *   const ids = items.map(i => i.id);
 *   const bulk = useBulkSelect(ids);
 *   <Checkbox checked={bulk.allSelected} onChange={() => bulk.toggleAll(ids)} />
 *   {items.map(it => (
 *     <tr>
 *       <Checkbox checked={bulk.isSelected(it.id)} onChange={() => bulk.toggle(it.id)} />
 *     </tr>
 *   ))}
 *   {bulk.count > 0 && <BulkActionBar count={bulk.count} onClear={bulk.clear} ... />}
 */
export default function useBulkSelect(visibleIds = []) {
  const [selected, setSelected] = useState(() => new Set());

  const isSelected = useCallback((id) => selected.has(id), [selected]);

  const toggle = useCallback((id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const clear = useCallback(() => setSelected(new Set()), []);

  const selectAll = useCallback((ids) => {
    setSelected(new Set(ids));
  }, []);

  const allSelected = useMemo(() => (
    visibleIds.length > 0 && visibleIds.every((id) => selected.has(id))
  ), [visibleIds, selected]);

  const someSelected = useMemo(() => (
    !allSelected && visibleIds.some((id) => selected.has(id))
  ), [allSelected, visibleIds, selected]);

  const toggleAll = useCallback((ids = visibleIds) => {
    const all = ids.every((id) => selected.has(id));
    if (all) clear();
    else selectAll(ids);
  }, [selected, clear, selectAll, visibleIds]);

  return {
    selected,
    isSelected,
    toggle,
    clear,
    selectAll,
    toggleAll,
    allSelected,
    someSelected,
    count: selected.size,
  };
}
